import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────
// Input (with InputGroup wrapper)
//
// Base input field. InputGroup wraps it with
// label, helper text, and error state.
// Design: §6.5 Form Controls
// ─────────────────────────────────────────────

// ── Base Input ───────────────────────────────
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  hasError?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, leadingIcon, trailingIcon, hasError, ...props }, ref) => {
    return (
      <div className="relative flex items-center">
        {leadingIcon && (
          <div className="pointer-events-none absolute left-3 flex items-center text-text-muted">
            {leadingIcon}
          </div>
        )}
        <input
          ref={ref}
          className={cn(
            "h-9 w-full rounded-control border bg-bg-input px-3 py-2",
            "text-[14px] text-text-primary placeholder:text-text-muted",
            "transition-colors focus:outline-none focus:ring-1",
            hasError
              ? "border-danger focus:border-danger focus:ring-danger"
              : "border-border-default focus:border-accent focus:ring-accent",
            leadingIcon && "pl-9",
            trailingIcon && "pr-9",
            className
          )}
          {...props}
        />
        {trailingIcon && (
          <div className="absolute right-3 flex items-center text-text-muted">
            {trailingIcon}
          </div>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";

// ── InputGroup (label + input + helper/error) ─
interface InputGroupProps extends InputProps {
  label?: string;
  helperText?: string;
  errorText?: string;
  required?: boolean;
}

const InputGroup = React.forwardRef<HTMLInputElement, InputGroupProps>(
  ({ label, helperText, errorText, required, id, className, ...inputProps }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, "-");
    const hasError = Boolean(errorText);

    return (
      <div className={cn("flex flex-col gap-[6px]", className)}>
        {label && (
          <LabelPrimitive.Root
            htmlFor={inputId}
            className="text-[14px] font-medium text-text-secondary"
          >
            {label}
            {required && <span className="ml-1 text-danger">*</span>}
          </LabelPrimitive.Root>
        )}

        <Input
          ref={ref}
          id={inputId}
          hasError={hasError}
          aria-describedby={
            errorText ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          aria-invalid={hasError}
          {...inputProps}
        />

        {errorText && (
          <p id={`${inputId}-error`} className="text-[12px] text-danger">
            {errorText}
          </p>
        )}
        {!errorText && helperText && (
          <p id={`${inputId}-helper`} className="text-caption text-text-muted">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);
InputGroup.displayName = "InputGroup";

export { Input, InputGroup };
